import type { RouteLocationRaw } from 'vue-router';

import { appConstants } from '@/appConstants';
import { TokenService } from '@/services';

const { AUTH_TOKEN_NAME, SCOPED_MERCHANT_LINK_KEYS, MERCHANT_LINK_JWT_CLAIM_KEYS } =
  appConstants;

function getActiveToken(): string | undefined {
  return (
    localStorage.getItem(AUTH_TOKEN_NAME) ||
    sessionStorage.getItem(AUTH_TOKEN_NAME) ||
    undefined
  );
}

export type ScopedLinkContext = {
  businessProcessId: string;
  merchantId?: string;
};

/**
 * Returns true when a JWT business-process claim represents a scoped link session.
 */
export function isScopedBusinessProcessId(value: string | undefined): value is string {
  return !!value && value.trim().length > 5;
}

/**
 * Returns true when the active token (or optional token argument) is scoped to a business process.
 */
export function isScopedMerchantLinkSession(token?: string): boolean {
  const activeToken: string | undefined = token ?? getActiveToken();

  if (!activeToken) {
    return false;
  }

  try {
    const payload: Record<string, string> = TokenService.payload(activeToken);
    return isScopedBusinessProcessId(
      payload[MERCHANT_LINK_JWT_CLAIM_KEYS.BUSINESS_PROCESS],
    );
  } catch {
    return false;
  }
}

/**
 * Parses merchant avid from the secure-link landing path (/merchants/:avid/...).
 */
export function extractMerchantAvidFromPath(pathname: string): string | undefined {
  const match: RegExpMatchArray | null = pathname.match(/^\/merchants\/([a-f0-9-]+)/i);
  return match?.[1];
}

/**
 * Parses business-process id from merchant business-process routes.
 */
export function extractBusinessProcessIdFromPath(pathname: string): string | undefined {
  const match: RegExpMatchArray | null = pathname.match(
    /^\/merchants\/[a-f0-9-]+\/business-processes\/([a-f0-9-]+)/i,
  );
  return match?.[1];
}

/**
 * Returns the business-process id claim from a JWT, when present.
 */
export function getBusinessProcessIdFromToken(token: string): string | undefined {
  try {
    const payload: Record<string, string> = TokenService.payload(token);
    return payload[MERCHANT_LINK_JWT_CLAIM_KEYS.BUSINESS_PROCESS]?.trim() || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Returns true when an existing scoped merchant-link session should exchange
 * a new link instead of reusing the stored token (different BP or stale storage).
 */
export function shouldReExchangeScopedMerchantLink(token: string, pathname: string): boolean {
  const tokenBusinessProcessId: string | undefined = getBusinessProcessIdFromToken(token);

  if (!isScopedBusinessProcessId(tokenBusinessProcessId)) {
    return false;
  }

  const pathBusinessProcessId: string | undefined = extractBusinessProcessIdFromPath(pathname);

  if (!pathBusinessProcessId) {
    return true;
  }

  return pathBusinessProcessId !== tokenBusinessProcessId;
}

/**
 * Persists scoped link context when the token contains a business-process claim.
 * Merchant id prefers previously stored session context; landing avid fills in only
 * when none is stored (e.g. first exchange after clearToken). Pathname must not
 * overwrite an established scoped merchant on later loads.
 * Clears scoped context when the claim is absent.
 */
export function syncScopedLinkFromToken(token: string, landingAvid?: string): void {
  const payload: Record<string, string> = TokenService.payload(token);
  const businessProcessId: string | undefined = payload[MERCHANT_LINK_JWT_CLAIM_KEYS.BUSINESS_PROCESS];

  if (!isScopedBusinessProcessId(businessProcessId)) {
    clearScopedLinkContext();
    return;
  }

  sessionStorage.setItem(SCOPED_MERCHANT_LINK_KEYS.BUSINESS_PROCESS_ID, businessProcessId.trim());

  const storedMerchantId: string | null = sessionStorage.getItem(
    SCOPED_MERCHANT_LINK_KEYS.MERCHANT_ID,
  );
  const merchantId: string | undefined =
    storedMerchantId?.trim() ||
    landingAvid?.trim() ||
    undefined;

  if (merchantId) {
    sessionStorage.setItem(SCOPED_MERCHANT_LINK_KEYS.MERCHANT_ID, merchantId);
  } else {
    sessionStorage.removeItem(SCOPED_MERCHANT_LINK_KEYS.MERCHANT_ID);
  }
}

/**
 * Returns active scoped link context from session storage, if any.
 */
export function getScopedLinkContext(): ScopedLinkContext | null {
  const businessProcessId: string | null = sessionStorage.getItem(
    SCOPED_MERCHANT_LINK_KEYS.BUSINESS_PROCESS_ID,
  );

  if (!isScopedBusinessProcessId(businessProcessId ?? undefined)) {
    return null;
  }

  const merchantId: string | undefined =
    sessionStorage.getItem(SCOPED_MERCHANT_LINK_KEYS.MERCHANT_ID)?.trim() || undefined;

  return {
    businessProcessId: businessProcessId ?? '',
    merchantId: merchantId || undefined,
  };
}

/**
 * Removes scoped link context from session storage.
 */
export function clearScopedLinkContext(): void {
  sessionStorage.removeItem(SCOPED_MERCHANT_LINK_KEYS.BUSINESS_PROCESS_ID);
  sessionStorage.removeItem(SCOPED_MERCHANT_LINK_KEYS.MERCHANT_ID);
}

/**
 * Clears SCOPED_* session keys then navigates.
 * Leftover keys make the router bounce Home/list breadcrumb pushes back to the BP page.
 */
export async function pushAfterClearingScopedLink(
  push: (to: RouteLocationRaw) => Promise<unknown>,
  to: RouteLocationRaw,
): Promise<void> {
  clearScopedLinkContext();
  await push(to);
}

/**
 * Returns true when the target route is allowed for a scoped link session.
 */
export function isRouteAllowedForScopedLink(
  routeName: string | symbol | null | undefined,
  params: { avid?: string; id?: string },
  scoped: ScopedLinkContext,
): boolean {
  if (routeName === 'error') {
    return true;
  }

  if (routeName === 'trust-us') {
    if (!params.avid) {
      return false;
    }

    if (scoped.merchantId && params.avid !== scoped.merchantId) {
      return false;
    }

    return true;
  }

  if (routeName !== 'merchant-business-process') {
    return false;
  }

  if (params.id !== scoped.businessProcessId) {
    return false;
  }

  if (scoped.merchantId && params.avid !== scoped.merchantId) {
    return false;
  }

  return true;
}

/**
 * Builds the redirect target for a scoped link session.
 */
export function getScopedLinkRedirectTarget(scoped: ScopedLinkContext): RouteLocationRaw {
  if (!scoped.merchantId) {
    return { name: 'error', params: { code: '403' } };
  }

  return {
    name: 'merchant-business-process',
    params: { avid: scoped.merchantId, id: scoped.businessProcessId },
  };
}

/**
 * Returns a redirect when navigation is blocked for a scoped link session.
 */
export function resolveScopedRouteRedirect(to: {
  name?: string | symbol | null | undefined;
  params: Record<string, unknown>;
}): RouteLocationRaw | null {
  if (!isScopedMerchantLinkSession()) {
    if (getScopedLinkContext()) {
      clearScopedLinkContext();
    }
    return null;
  }

  const scoped: ScopedLinkContext | null = getScopedLinkContext();

  if (!scoped) {
    return null;
  }

  const params: { avid?: string; id?: string } = {
    avid: to.params.avid as string | undefined,
    id: to.params.id as string | undefined,
  };

  if (isRouteAllowedForScopedLink(to.name, params, scoped)) {
    return null;
  }

  return getScopedLinkRedirectTarget(scoped);
}
